import { DownloadOutlined } from "@ant-design/icons";
import { Button, Divider, Spin, Tag, Tooltip } from "antd";
import maplibregl from "maplibre-gl";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ContentLayoutManagement from "../../../../layouts/ContentLayoutManagement";
import HeaderContent from "../../../../layouts/HeaderContent";
import { getOrderDownloadJobUrl } from "../../../../services/download/downloadJobService";
import
{
   getDetailOrderTaskingByUerAsync,
   updateStatusOrderAsync,
} from "../../../../store/reducers/orderSlice";

import CommonComponents from "../../../../components/Commons";
import useToggle from "../../../../hooks/useToggle";

import
{
   flyToGeometry,
   formatValue,
} from "../../../../components/VMRS/ExploreArchive/ultis";
import { AOI_FEATURE_KEY } from "../../../../components/VMRS/MapAdapter/plugins/aoi/consts";
import { Row, Section } from "../../../../components/VMRS/Order/DetailSection";
import PolygonInfo from "../../../../components/VMRS/Order/Tasking/PolygonInfo";
import { resolveOrderListCrumb } from "../../../../constants/routePaths";
import { ESttausCode } from "../../../../enums";
import { getErrorMsg } from "../../../../helpers/common";
import { showError, showSuccess } from "../../../../helpers/toast";
import { setLoadingGlobal } from "../../../../store/reducers/appCommonSlice";
import { addDownloads, removeDownload, setStatus, updateProgress } from "../../../../store/reducers/downloadSlice";
import { RootState } from "../../../../store/store";
import { downloadManager } from "../../../../utils/downloadManager";


import ImageInfo from "../../../../components/VMRS/ExploreArchive/ImageInfo";
import
{
   dataKeyGeneral,
   formatLabelWithUnit,
} from "../../../../components/VMRS/ExploreTasking/ultis";
import ImagesMapVMRS from "../../../../components/VMRS/MapAdapter/ImagesMap";
import { changeSourceData } from "../../../../components/VMRS/MapAdapter/plugins/draw";
import { clearAOIFeatures, setDrawingAOIMode } from "../../../../components/VMRS/MapAdapter/store/slices/aoiSlice";
import
{
   clearFeatures,
   setDrawingMode,
   updateFeature
} from "../../../../components/VMRS/MapAdapter/store/slices/drawSlice";
import { clearAllLayers } from "../../../../components/VMRS/MapAdapter/store/slices/layerSlice";
import { setMeasureMode } from "../../../../components/VMRS/MapAdapter/store/slices/measureSlice";
import { loadBasemaps } from "../../../../components/VMRS/MapAdapter/store/thunks/loadBasemaps";
import { useFetchDataRealtimeSync } from "../../../../components/VMRS/Order/hooks/useFetchDataRealtime";
import CartImageOrder from "../../../../components/VMRS/Order/Tasking/CartImageOrder";
import { ReasonCancelReject } from "../../../../components/VMRS/Order/Tasking/common";

export default function OrderTaskingDetail()
{
   const { t } = useTranslation();
   const { id } = useParams();
   const dispatch = useDispatch();
   const navigate = useNavigate();
   const location = useLocation();

   const [loading, setLoading] = useState(true);
   const [data, setData] = useState<any>({});
   const [reason, setReason] = useState<string>("");
   const [engineMap, setEngineMap] = useState<maplibregl.Map | undefined>(
      undefined,
   );
   const [selectedView, setSelectedView] = useState<any>(undefined);

   // Point AOI (e.g. Satellogic orders) is shown as a real map pin rather than
   // the draw plugin's generic small dot — tracked here so a re-fetch (or
   // navigating to another order) replaces it instead of stacking markers.
   const pointMarkerRef = useRef<maplibregl.Marker | null>(null);

   const notifications = useSelector(
      (state: RootState) => state.notificationSlice.total,
   );

   const downloadingFiles = useSelector(
      (state: RootState) => state.download.downloadingFiles,
   );
   const dataStatusOrder = useSelector(
      (state: RootState) => state.appCommonSlice.dataStatusOrder,
   );

   const {
      mapType,
      view: mapView,
      basemaps,
      selectedBasemap,
   } = useSelector((state: RootState) => state.map);


   const getData = async (isActive: () => boolean = () => true) =>
   {
      try
      {
         setLoading(true);

         // Clear any polygon/marker from a previously viewed order. The component
         // stays mounted when navigating between orders (only the :id param
         // changes), so without this the old order's feature accumulates and
         // gets redrawn.
         dispatch(clearFeatures());
         changeSourceData(engineMap, []);
         pointMarkerRef.current?.remove();
         pointMarkerRef.current = null;

         let res = // @ts-ignore
            await dispatch(getDetailOrderTaskingByUerAsync(id)).unwrap();

         // Bail if the user navigated to another order while this request was in
         // flight — otherwise a slow response could draw a stale order's polygon.
         if (!isActive()) return;

         setData(res?.data?.data ?? {});

         // Geometry is read from request_body (common across providers).
         const firstItem = res?.data?.data?.items?.[0];
         const geometry = firstItem?.request_body?.geometry;

         if (geometry)
         {
            const fill = "rgba(255, 255, 0, 0.9)";

            if (geometry.type === "Point" && engineMap)
            {
               // Point AOI (e.g. Satellogic orders) — shown as a real pin, not
               // the draw plugin's generic small dot.
               pointMarkerRef.current = new maplibregl.Marker({ color: fill })
                  .setLngLat(geometry.coordinates)
                  .addTo(engineMap);
            } else
            {
               const featureId = `${AOI_FEATURE_KEY}-${res?.data?.data?.order_number}`;
               const stroke = "rgba(255, 255, 0, 1)";

               dispatch(updateFeature({ id: featureId, geometry, fill, stroke }));
               changeSourceData(engineMap, [{ id: featureId, geometry, fill, stroke }]);
            }

            flyToGeometry(geometry, engineMap);
         }
         setLoading(false);
      } catch (error)
      {
      } finally
      {
         setLoading(false);
      }
   };

   useEffect(() =>
   {
      if (!engineMap || !id) return;

      let active = true;
      getData(() => active);
      return () =>
      {
         active = false;
      };
   }, [id, engineMap]);


   useEffect(() =>
   {
      return () =>
      {
         dispatch(clearFeatures());
         pointMarkerRef.current?.remove();
         pointMarkerRef.current = null;
      };
   }, []);

   useEffect(() =>
   {
      // @ts-ignore – thunk
      dispatch(loadBasemaps());
   }, []);


   const handleChangeStatusOrder = async (
      idOrder: number,
      statusName: string,
      note: string,
      toggleCallback?: () => void,
   ) =>
   {
      try
      {
         setLoading(true);
         dispatch(setLoadingGlobal(true));
         toggleCallback?.();

         const statusId = dataStatusOrder?.find(
            (itemF: any) => itemF?.key === statusName,
         )?.id;

         // @ts-ignore
         const result = await dispatch(
            // @ts-ignore
            updateStatusOrderAsync({
               order_id: id,
               status_id: statusId,
               notes: note,
            }),
         ).unwrap();

         if (result.status === ESttausCode.OK)
         {
            showSuccess(t("Successfully"));

            setData((prev: any) => ({
               ...prev,
               status: {
                  ...prev.status,
                  key: dataStatusOrder?.find(
                     (itemF: any) => itemF?.key === statusName,
                  )?.key,
                  id: dataStatusOrder?.find((itemF: any) => itemF?.key === statusName)
                     ?.id,
                  name: dataStatusOrder?.find(
                     (itemF: any) => itemF?.key === statusName,
                  )?.name,
                  color: dataStatusOrder?.find(
                     (itemF: any) => itemF?.key === statusName,
                  )?.color,
               },
            }));
            // getData();
         }
      } catch (error)
      {
         showError(t(getErrorMsg(error)));
      } finally
      {
         dispatch(setLoadingGlobal(false));
         setLoading(false);
      }
   };

   const {
      open: openCancelOrder,
      toggle: toggleCancelOrder,
      shouldRender: shouldRenderCancelOrder,
   } = useToggle();

   const {
      open: openSendOrderUser,
      toggle: toggleSendOrderUser,
      shouldRender: shouldSendOrderUser,
   } = useToggle();

   const {
      open: openConfirmPrice,
      toggle: toggleConfirmPrice,
      shouldRender: shouldConfirmPrice,
   } = useToggle();

   const {
      open: openCancelPrice,
      toggle: toggleCancelPrice,
      shouldRender: shouldCancelPrice,
   } = useToggle();

   const {
      open: openViewInfo,
      shouldRender: shouldRenderViewInfo,
      toggle: toggleViewInfo,
   } = useToggle();

   const {
      open: openDownloadData,
      shouldRender: shouldDownloadData,
      toggle: toggleDownloadData,
   } = useToggle();

   const handleSendOrderUser = () =>
   {
      handleChangeStatusOrder(Number(id), "IN_PROGRESS", "", toggleSendOrderUser);
   };

   const handleCancel = () =>
   {
      handleChangeStatusOrder(Number(id), "CANCELED", reason, toggleCancelOrder);
   };

   const handleConfirmPrice = () =>
   {
      handleChangeStatusOrder(
         Number(id),
         "CONFIRMED_QUOTATION",
         "",
         toggleConfirmPrice,
      );
   };

   const handleDownloadData = () =>
   {
      // const orderItemId = data?.items?.[0]?.id;
      const orderItemId = data?.id
      if (!orderItemId)
      {
         showError(t("No files available for download"));
         return;
      }

      // The endpoint returns a zip job, not a file. downloadManager polls it and
      // opens the presigned URL once the job reports READY; the tray shows the
      // "preparing"/"compressing" phases meanwhile.
      const file: any = {
         id: `file-${Date.now()}`,
         name: data?.name,
         url: getOrderDownloadJobUrl(orderItemId),
         progress: 0,
         status: "preparing" as const,
      };

      dispatch(addDownloads([file]));
      downloadManager.downloadFile(file);
      toggleDownloadData();
   };

   const handleDownloadDataBackup = () =>
   {
      const orderItemId = data?.items?.[0]?.id;
      // const orderItemId = data?.id
      if (!orderItemId)
      {
         showError(t("No files available for download"));
         return;
      }

      // Same zip-job flow as handleDownloadData, keyed on the order item.
      const file: any = {
         id: `file-${Date.now()}`,
         name: data?.name,
         url: getOrderDownloadJobUrl(orderItemId),
         progress: 0,
         status: "preparing" as const,
      };

      dispatch(addDownloads([file]));
      downloadManager.downloadFile(file);
      toggleDownloadData();
   };

   const handleDownloadAOI = () =>
   {
      const geometry = data?.items?.[0]?.request_body?.geometry;
      if (!geometry)
      {
         showError(t("No AOI available for download"));
         return;
      }

      const fileId = `aoi-${Date.now()}`;
      const fileName = `AOI_${data?.order_number ?? id}.geojson`;

      // Client-side generation has no real progress, but pushing it through the
      // same tray as handleDownloadData keeps download feedback consistent.
      dispatch(
         addDownloads([
            { id: fileId, name: fileName, url: "", progress: 0, status: "downloading" },
         ]),
      );

      try
      {
         const geojson = {
            type: "Feature",
            properties: { order_number: data?.order_number },
            geometry,
         };

         const blob = new Blob([JSON.stringify(geojson, null, 2)], {
            type: "application/geo+json",
         });
         const url = URL.createObjectURL(blob);

         const link = document.createElement("a");
         link.href = url;
         link.download = fileName;
         document.body.appendChild(link);
         link.click();
         document.body.removeChild(link);
         URL.revokeObjectURL(url);

         dispatch(updateProgress({ id: fileId, progress: 100 }));
         dispatch(setStatus({ id: fileId, status: "completed" }));
      } catch (error)
      {
         dispatch(setStatus({ id: fileId, status: "error" }));
      } finally
      {
         setTimeout(() => dispatch(removeDownload(fileId)), 2000);
      }
   };

   const handleCancelPrice = () =>
   {
      handleChangeStatusOrder(
         Number(id),
         "REJECTED_QUOTATION",
         "",
         toggleCancelPrice,
      );
   };

   const handleViewInfo = (item?: any) =>
   {
      setSelectedView(item);
      toggleViewInfo();
   };

   useFetchDataRealtimeSync({
      notifications,
      getData,
      path: `order/management-tasking/${id}`,
   });

   const handleResetMap = () =>
   {
      dispatch(clearAOIFeatures());
      dispatch(clearAllLayers());
      dispatch(setDrawingAOIMode("NONE"));
      dispatch(setDrawingMode("NONE"));
      dispatch(setMeasureMode("NONE"));
   };

   useEffect(() =>
   {
      // Reset AOI/layers/draw modes whenever the viewed order changes (the
      // component stays mounted across orders), so images/footprints shown for a
      // previous order don't linger on the map.
      handleResetMap();

      return () =>
      {
         handleResetMap();
      };
   }, [id]);

   // The detail page answers to both the tasking and the monitoring list, so the
   // crumb has to point back at the list the current URL belongs to.
   const listCrumb = resolveOrderListCrumb(location.pathname);

   const buyerName = (() =>
   {
      const b = data?.buyer?.name;
      if (!b) return undefined;
      return [b.first, b.last].filter(Boolean).join(" ");
   })();

   return (
      <ContentLayoutManagement
         header={
            <HeaderContent
               breadcrumb={[
                  {
                     title: t(listCrumb.labelKey),
                     path: `${listCrumb.path}${location.search}`,
                  },
                  {
                     title: data?.name,
                  },
               ]}
            />
         }
      >
         <div className="h-full flex bg-[var(--neutral-1000)] text-white">
            <div className="relative w-5/12 min-w-[380px] shrink-0 flex flex-col border-r border-[#26324a]  pt-6">
               <Spin spinning={loading}>
                  <div
                     className="overflow-auto order-detail-scroll"
                     style={{
                        height:
                           data?.status?.key === "ORDERED" ||
                              data?.status?.key === "CONFIRMING_QUOTATION"
                              // || data?.status?.key === "CLOSED" || data?.status?.key === "READY_DOWNLOAD"
                              ? "calc(100% - 65px)"
                              : "100%",
                     }}
                  >
                     {/* Status + Info */}
                     <div className="order-status-grid px-6 gap-2 mb-4">
                        <div className="bg-[var(--neutral-950)] rounded-md p-3 text-left">
                           <div className="text-xs text-slate-400 mb-3">
                              {t("Status")}
                           </div>


                           <Tag
                              color={data?.status?.color}
                              className="px-3 py-1 rounded-full !text-white flex !justify-center"
                           >
                              {t(data?.status?.name)}
                           </Tag>
                        </div>
                        <div className="bg-[var(--neutral-950)] rounded-md p-3 text-left">
                           <div className="text-xs text-slate-400 mb-3">
                              {t("Provider")}
                           </div>
                           <div className="text-sm">
                              {data?.items?.[0]?.provider?.name}
                           </div>
                        </div>
                        <div className="bg-[var(--neutral-950)] rounded-md p-3 text-left">
                           <div className="text-xs text-slate-400 mb-3">
                              {t("Satellites")}
                           </div>
                           <div className="text-sm">{data?.satellites?.length > 0 ? data?.satellites?.join(", ") : data?.items?.[0]?.provider?.name}</div>
                        </div>
                     </div>
                     {/* Thông tin đặt ảnh */}
                     <Section title={t("General Information Request")}>
                        <Row
                           label={t("Order name")}
                           value={data?.items?.[0]?.request_body?.order_name ?? "--"}
                        />
                        <Row
                           label={t("Reason")}
                           value={data?.items?.[0]?.request_body?.reason}
                        />
                        <Row
                           label={t("Priority")}
                           value={t(data?.items?.[0]?.request_body?.priority)}
                           highlight
                        />
                        <Row
                           label={t("Description")}
                           value={data?.items?.[0]?.request_body?.description_order}
                        />

                        <Divider />
                        <Row label={t("Buyer")} value={buyerName} />
                        <Row label={t("Email")} value={data?.buyer?.email} />

                        {/* {data?.items?.[0]?.company_price &&
                           data?.items?.[0]?.company_price !== undefined && (
                              <>
                                 <Divider />
                                 <Row
                                    label={t("Price")}
                                    highlight
                                    value={
                                       data?.items?.[0]?.company_price
                                          ? new Intl.NumberFormat("en-US", {
                                             style: "currency",
                                             currency:
                                                data?.items?.[0]?.currency_company || "USD",
                                          }).format(data?.items?.[0]?.company_price)
                                          : "--"
                                    }
                                 />
                              </>
                           )} */}

                        {data?.notes && data?.notes !== undefined && (
                           <>
                              <Divider />
                              <Row label={t("Notes")} warning value={data?.notes} />
                           </>
                        )}
                     </Section>
                     {/* Thông số chụp ảnh */}
                     <Section title={t("Order form")}>
                        {data?.items?.length > 0 &&
                           data?.items?.[0]?.request_body &&
                           Object.entries(data?.items?.[0]?.request_body)?.length >
                           0 && (
                              <>
                                 {Object.entries(data?.items?.[0]?.request_body)
                                    ?.filter((itemF?: any) =>
                                    {
                                       const [key, value] = itemF;
                                       if (dataKeyGeneral.includes(key)) return false;
                                       if (value === null || value === undefined)
                                          return false;
                                       if (typeof value === "string" && value.trim() === "")
                                          return false;
                                       if (Array.isArray(value) && value.length === 0)
                                          return false;
                                       if (
                                          typeof value === "object" &&
                                          !Array.isArray(value) &&
                                          Object.keys(value).length === 0
                                       )
                                          return false;
                                       return true;
                                    })
                                    .sort(([a], [b]) => a.localeCompare(b))
                                    ?.map(([key, value]) => (
                                       <Row
                                          key={key}
                                          label={formatLabelWithUnit(key, t)}
                                          value={
                                             value !== null && typeof value === "object"
                                                ? JSON.stringify(value)
                                                : formatValue(value, key)
                                          }
                                       />
                                    ))}
                              </>
                           )}
                     </Section>
                     {data?.items?.[0]?.stac_items?.length > 0 && <Section title={t("Images in order")}>
                        {data?.items?.[0]?.stac_items?.map((image: any) => <CartImageOrder key={image?.id} item={image} map={engineMap} providerCode={data?.items?.[0]?.provider_code} orderItemId={data?.items?.[0]?.id} handleViewInfo={() => handleViewInfo(image)} />)}
                     </Section>}
                     {/* Thông tin đa giác */}
                     <Section title={t("Polygon information")}>
                        <PolygonInfo
                           selectedPolygon={data?.items?.[0]?.request_body?.geometry}
                           t={t}
                        />
                     </Section>
                  </div>

                  {(data?.status?.key === "ORDERED" ||
                     data?.status?.key === "CONFIRMING_QUOTATION"
                     // || data?.status?.key === "CLOSED" ||
                     // data?.status?.key === "READY_DOWNLOAD"
                  ) && (
                        <div
                           className="order-footer flex flex-wrap absolute bottom-0 w-full justify-between gap-2 px-6 py-4 bg-[var(--neutral-1000)]"
                           style={{ borderTop: "1px solid var(--divider-1)" }}
                        >
                           {data?.status?.key === "ORDERED" && (
                              <>
                                 <div>
                                    <span className="min-w-[110px] block">
                                       <Button
                                          onClick={() => toggleCancelOrder()}
                                          className="w-full !bg-[none] border border-[#FA5343] text-[#FA5343]"
                                          style={{ background: "none" }}
                                          disabled={loading}
                                       >
                                          {t("Cancel request")}
                                       </Button>
                                    </span>
                                 </div>
                                 <div className="flex gap-2">
                                    <span className="block">
                                       <Button
                                          className="w-full border border-[var(--primary-4)] !bg-[none] !text-[var(--primary-4)]"
                                          style={{ background: "none" }}
                                          onClick={() => navigate(`./edit${location.search}`)}
                                          disabled={loading}
                                       >
                                          {t("Edit")}
                                       </Button>
                                    </span>
                                    <span className="min-w-[110px] block">
                                       <Button
                                          onClick={() => toggleSendOrderUser()}
                                          className="w-full !border-none !bg-[var(--primary-4)] text-[var(--primary-950)]"
                                          disabled={loading}
                                       >
                                          {t("Send request")}
                                       </Button>
                                    </span>
                                 </div>
                              </>
                           )}

                           {data?.status?.key === "CONFIRMING_QUOTATION" && (
                              <>
                                 <div className="flex gap-2 justify-end flex-wrap w-full">
                                    <span className="block">
                                       <Button
                                          className="w-full border border-[var(--primary-4)] !bg-[none] !text-[var(--primary-4)]"
                                          style={{ background: "none" }}
                                          onClick={() => toggleCancelPrice()}
                                          disabled={loading}
                                       >
                                          {t("Cancel quotation")}
                                       </Button>
                                    </span>
                                    <span className="min-w-[110px] block">
                                       <Button
                                          onClick={() => toggleConfirmPrice()}
                                          className="w-full !border-none !bg-[var(--primary-4)] text-[var(--primary-950)]"
                                          disabled={loading}
                                       >
                                          {t("Accept quotation")}
                                       </Button>
                                    </span>
                                 </div>
                              </>
                           )}

                           {/* {(data?.status?.key === "CLOSED" || data?.status?.key === "READY_DOWNLOAD") && data?.items?.[0]?.stac_items?.length > 0 && (
                              <div className="flex gap-2 justify-end flex-wrap w-full">
                                 <Tooltip
                                    title={
                                       downloadingFiles.length > 0
                                          ? t("A file is being downloaded, please wait")
                                          : ""
                                    }
                                 >
                                    <span className="min-w-[110px] block">
                                       <Button
                                          onClick={() => toggleDownloadData()}
                                          className="w-full !border-none !bg-[var(--primary-4)] text-[var(--primary-950)]"
                                          disabled={loading || downloadingFiles.length > 0}
                                       >
                                          {t("Download data")}
                                       </Button>
                                    </span>
                                 </Tooltip>
                              </div>
                           )} */}
                        </div>
                     )}
               </Spin>
            </div>

            <div className="flex-1 min-w-0 w-7/12 relative">
               {selectedBasemap && (
                  <ImagesMapVMRS
                     mapType={mapType}
                     showBaseMap
                     showMapControl
                     view={mapView}
                     onSetEngineMap={(map: any) =>
                     {
                        if (!map) return;
                        // setEngineMap only after map is fully loaded so fly2GeometryList has a ready engine
                        map.loaded()
                           ? setEngineMap(map)
                           : map.once("load", () => setEngineMap(map));
                     }}
                  >
                     {data?.items?.[0]?.request_body?.geometry && (
                        <Button
                           className="button-map"
                           icon={<DownloadOutlined />}
                           onClick={handleDownloadAOI}
                        >
                           {t("Download AOI")}
                        </Button>
                     )}
                  </ImagesMapVMRS>
               )}
            </div>

            {shouldRenderCancelOrder && (
               <CommonComponents.ModalUI
                  title={t("Confirm")}
                  open={openCancelOrder}
                  width={"450px"}
                  toggle={toggleCancelOrder}
                  handleConfirm={() =>
                  {
                     if (reason?.trim().length > 0)
                     {
                        handleCancel();
                     }
                  }}
                  disabled={reason?.trim().length === 0}
               >
                  <p className="mb-1">
                     {t("Are you sure you want to cancel this image's request?")}
                  </p>
                  <ReasonCancelReject reason={reason} setReasson={setReason} t={t} />
               </CommonComponents.ModalUI>
            )}

            {shouldSendOrderUser && (
               <CommonComponents.ModalUI
                  title={t("Confirm")}
                  open={openSendOrderUser}
                  toggle={toggleSendOrderUser}
                  handleConfirm={handleSendOrderUser}
               >
                  <p>
                     {t(
                        "Are you sure you want to send this image's request to admin?",
                     )}
                  </p>
               </CommonComponents.ModalUI>
            )}

            {shouldConfirmPrice && (
               <CommonComponents.ModalUI
                  title={t("Confirm quotation")}
                  open={openConfirmPrice}
                  toggle={toggleConfirmPrice}
                  handleConfirm={handleConfirmPrice}
               >
                  <p>{t("Are you sure you want to confirm price's this order?")}</p>
               </CommonComponents.ModalUI>
            )}

            {shouldCancelPrice && (
               <CommonComponents.ModalUI
                  title={t("Cancel order")}
                  open={openCancelPrice}
                  toggle={toggleCancelPrice}
                  handleConfirm={handleCancelPrice}
               >
                  <p>{t("Are you sure you want to cancel this order?")}</p>
               </CommonComponents.ModalUI>
            )}

            {shouldDownloadData && (
               <CommonComponents.ModalUI
                  title={t("Confirm")}
                  open={openDownloadData}
                  toggle={toggleDownloadData}
                  handleConfirm={handleDownloadData}
               >
                  <p>{t("Are you sure you want to download this data?")}</p>
               </CommonComponents.ModalUI>
            )}

            {shouldRenderViewInfo && (
               <CommonComponents.ModalUI
                  open={openViewInfo}
                  title={t("Information")}
                  toggle={() =>
                  {
                     toggleViewInfo();
                  }}
                  handleConfirm={() => { }}
                  hiddenFooter={true}
                  width={1120}
               >

                  <ImageInfo data={selectedView} open={openViewInfo} />

               </CommonComponents.ModalUI>
            )}
         </div>
      </ContentLayoutManagement>
   );
}
